import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import React, {useEffect, useMemo} from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {ActionItem, ActionMenu} from "@khanacademy/wonder-blocks-dropdown";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    HeadingSmall,
    LabelLarge,
    LabelMedium,
} from "@khanacademy/wonder-blocks-typography";
import {
    enums,
    publishAnalyticsEventForMinorConversion,
    useMarkConversion,
    withConversionContext,
} from "@khan/analytics-learning-time";
import {SimpleTable, type Column} from "@khan/component-table";
import {useAIGuideToolPersona} from "@khan/guide-core-helpers";
import {ToolPersona} from "@khan/khanmigo-tools";
import {useLocation} from "@khan/urls";

import useProfile from "#data-access/use-profile.ts";
import {CANVAS_INTEGRATION_PATH} from "#guide-canvas-integration/const.ts";
import {PARENT_KHANMIGO_TOOLS_PATH} from "#page-package/util.ts";

import {Tabs, type TabId} from "../khanmigo/ai-tools-and-docs.tsx";
import {getAdminTools, getTools, type Tool} from "../khanmigo/ai-tools-list.ts";
import {TEACHER_KHANMIGO_TOOLS_PATH} from "../khanmigo/path-helpers.ts";
import {Platform} from "../khanmigo/utils.ts";

import {strings} from "./constants.ts";
import GetShareLinkModal from "./get-share-link-modal.tsx";
import {useGetAiGuideThreadsDataQuery} from "./graphql/ai-guide-threads-query.ts";
import MyDocumentsArchiveModal from "./my-documents-archive-modal.tsx";
import {createResumeDocumentHref, formatDate} from "./utils.ts";

import dotsThreeIcon from "@phosphor-icons/core/regular/dots-three.svg";
import pencilIcon from "@phosphor-icons/core/regular/pencil.svg";

const getHrefAndOnClick = (
    path: string,
    setTab: (tab: TabId) => void,
    platform: Platform,
    persona: ToolPersona,
): {href: string | undefined; onClick: (() => void) | undefined} => {
    const toolsPath =
        persona === ToolPersona.Parent
            ? PARENT_KHANMIGO_TOOLS_PATH
            : TEACHER_KHANMIGO_TOOLS_PATH;

    // We have slightly different URLs depending on the platform.
    const toolsPageUrl =
        platform === Platform.Canvas ? CANVAS_INTEGRATION_PATH : toolsPath;

    // If we are already on the Khanmigo Tools page, then we need to switch
    // tabs with an onClick. Otherwise, we navigate to the correct URL with a
    // query parameter.
    if (path === toolsPageUrl) {
        return {
            href: undefined,
            onClick: () => setTab(Tabs.AI_TOOLS),
        };
    }
    return {
        href: `${toolsPageUrl}?tab=${Tabs.AI_TOOLS}`,
        onClick: undefined,
    };
};

type StateProps = {
    state: "empty" | "error";
    setTab: (tab: TabId) => void;
    platform: Platform;
};

const StateComponent = ({state, setTab, platform}: StateProps) => {
    const persona = useAIGuideToolPersona();

    const title =
        state === "empty"
            ? t`You do not have any saved documents yet.`
            : t`Something went wrong.`;
    const subtitle =
        state === "empty"
            ? t`Try one of our AI-powered tools to create anything from lesson plans to class newsletters.`
            : t`Try again or try one of our AI-powered tools to create anything from lesson plans to class newsletters.`;

    const {pathname} = useLocation();
    const {href, onClick} = getHrefAndOnClick(
        pathname,
        setTab,
        platform,
        persona,
    );
    return (
        <View style={styles.emptyContainer}>
            <HeadingSmall>{title}</HeadingSmall>
            <Strut size={spacing.xSmall_8} />
            <View>{subtitle}</View>
            <Strut size={spacing.large_24} />
            <Button href={href} onClick={onClick}>
                {t`Explore Khanmigo Tools`}
            </Button>
        </View>
    );
};

type KhanmigoDocument = {
    id: string;
    title: string;
    configName: string;
    threadId: string;
    persona: ToolPersona;
    lastUpdatedTime: string;
};

/**
 * A cell in the table that contains the action menu for each document. It
 * contains the "Remove" and "Share" actions.
 */
function DocumentActions({
    document,
    onArchive,
    tool,
}: {
    document: KhanmigoDocument;
    onArchive: (threadId: string) => void;
    tool?: Tool;
}) {
    const [archiveModalOpened, setArchiveModalOpened] = React.useState(false);
    const [shareModalOpened, setShareModalOpened] = React.useState(false);

    const {configName, title, threadId} = document;

    const onCopyLinkClick = () => {
        publishAnalyticsEventForMinorConversion({
            eventType: "AiGuideTeacherDashboardDocumentsLinkCopied",
            eventSchemaVersion: 1,
            documentId: threadId,
            aiGuideExperienceReportingLabelId: tool?.reportingLabelId ?? "",
        });
    };

    const onShareClick = () => {
        publishAnalyticsEventForMinorConversion({
            eventType: "AiGuideTeacherDashboardDocumentsShareSelected",
            eventSchemaVersion: 1,
            documentId: threadId,
            aiGuideExperienceReportingLabelId: tool?.reportingLabelId ?? "",
        });
    };

    // NOTE: The following ModalLauncher instances are defined separately from
    // the ActionMenu to avoid the need to re-render the ActionMenu when the
    // modal state changes and also to avoid potentially having event handler
    // collisions between the ActionMenu and ModalLauncher.
    return (
        <>
            <ModalLauncher
                opened={archiveModalOpened}
                onClose={() => {
                    setArchiveModalOpened(false);
                }}
                modal={({closeModal}) => (
                    <MyDocumentsArchiveModal
                        closeModal={(archived) => {
                            if (archived) {
                                onArchive(threadId);
                            }
                            closeModal();
                        }}
                        threadId={threadId}
                        documentName={title}
                        configName={configName}
                    />
                )}
            />

            <ModalLauncher
                opened={shareModalOpened}
                onClose={() => {
                    setShareModalOpened(false);
                }}
                modal={({closeModal}) => (
                    <GetShareLinkModal
                        title={title}
                        closeModal={closeModal}
                        documentID={threadId}
                        onCopyLinkClick={onCopyLinkClick}
                        persona={document.persona}
                    />
                )}
            />

            <ActionMenu
                alignment="right"
                dropdownStyle={styles.kebabMenu}
                menuText="actions"
                opener={() => (
                    <IconButton
                        aria-label={t`Actions`}
                        aria-haspopup="true"
                        icon={dotsThreeIcon}
                        kind="tertiary"
                        actionType="neutral"
                        size="small"
                        testId="documents-table-kebab-menu"
                    />
                )}
            >
                <ActionItem
                    label={t`Remove`}
                    onClick={() => {
                        setArchiveModalOpened(true);
                    }}
                />

                {document.configName !== "teacher-tool-blooket-generator" && (
                    <ActionItem
                        label={strings().share}
                        onClick={() => {
                            setShareModalOpened(true);
                            onShareClick();
                        }}
                    />
                )}
            </ActionMenu>
        </>
    );
}

type Props = {
    setTab: (tab: TabId) => void;
    platform: Platform;
    persona?: ToolPersona;
};

/**
 * A component that displays the user's Khanmigo documents.
 */
const MyDocuments = ({platform, setTab, persona}: Props): React.ReactNode => {
    const [archivedThreadIDs, setArchivedThreadIDs] = React.useState<
        Set<string>
    >(new Set());
    const profileResult = useProfile();
    const kaid =
        profileResult.status === "success"
            ? profileResult.profile?.get("kaid") || ""
            : "";

    const {threads, status} = useGetAiGuideThreadsDataQuery(kaid, true, 100);

    const markConversion = useMarkConversion();

    const filteredThreads = useMemo(() => {
        return threads
            ?.filter((thread) => {
                const isBlooketQuestionSet =
                    thread?.blooketQuestionSet?.title &&
                    thread?.blooketQuestionSet.threadId;

                const isCoeditingDocument =
                    thread?.coeditingDocument?.configName &&
                    thread?.coeditingDocument?.threadId;

                return (
                    (isBlooketQuestionSet || isCoeditingDocument) &&
                    !archivedThreadIDs.has(thread.id)
                );
            })
            .map((thread) => {
                if (thread?.coeditingDocument) {
                    return {
                        id: thread.coeditingDocument.id,
                        title: thread.coeditingDocument.title,
                        configName: thread.coeditingDocument.configName,
                        threadId: thread.coeditingDocument.threadId,
                        persona: thread.coeditingDocument.persona,
                        lastUpdatedTime:
                            thread.coeditingDocument.latestSnapshot.createdAt,
                    } as KhanmigoDocument;
                }

                if (thread?.blooketQuestionSet) {
                    return {
                        id: thread.blooketQuestionSet.id,
                        title: thread.blooketQuestionSet.title,
                        configName: "teacher-tool-blooket-generator",
                        threadId: thread.blooketQuestionSet.threadId,
                        persona: thread.blooketQuestionSet.persona,
                        // TO DO (bcdirito):
                        // Add lastUpdatedAt field to BlooketQuesitonSet entity
                        // post-Blooket MVE launch
                        lastUpdatedTime:
                            thread?.blooketQuestionSet.lastUpdatedAt ?? "",
                    } as KhanmigoDocument;
                }

                return {
                    id: "",
                    title: "",
                    configName: "",
                    threadId: "",
                    lastUpdatedTime: "",
                } as KhanmigoDocument;
            });
    }, [threads, archivedThreadIDs]);

    const tools =
        persona === ToolPersona.Admin
            ? getAdminTools().concat(getTools())
            : getTools();

    useEffect(() => {
        markConversion({
            id: "pageview_my_documents",
        });
    }, [markConversion]);

    const columns: Array<Column<KhanmigoDocument>> = useMemo(() => {
        const findTool = (configName: string) =>
            tools.find((tool: Tool) => tool.configName === configName);

        return [
            {
                label: t`Name`,
                sortable: "ascending",
                dataKey: "title",
                cell: (document: KhanmigoDocument) => {
                    const {configName, persona, threadId, title} = document;
                    const tool = findTool(configName);
                    const urlSlug = tool?.urlSlug ?? "";
                    const icon = tool?.icon ?? pencilIcon;
                    const iconColor = tool?.color ?? color.fadedPurple24;

                    return (
                        <Link
                            href={createResumeDocumentHref(
                                urlSlug ?? "",
                                threadId,
                                persona,
                                platform,
                            )}
                            target={
                                platform === Platform.Canvas
                                    ? undefined
                                    : "_blank"
                            }
                            style={styles.links}
                            testId="document-link"
                        >
                            <LabelLarge style={styles.labelContainer}>
                                <View
                                    style={[
                                        styles.iconContainer,
                                        {backgroundColor: iconColor},
                                    ]}
                                >
                                    <PhosphorIcon role="img" icon={icon} />
                                </View>
                                <Strut size={spacing.xSmall_8} />
                                {title ?? ""}
                            </LabelLarge>
                        </Link>
                    );
                },
            },
            {
                label: t`Type`,
                dataKey: "coeditingDocument.configName",
                cell: (document: KhanmigoDocument) => {
                    const {configName} = document;

                    const tool = findTool(configName);
                    const title = tool?.title ?? "";
                    return (
                        <LabelMedium style={styles.labelMedium}>
                            {title}
                        </LabelMedium>
                    );
                },
            },
            {
                label: t`Last Modified`,
                sortable: "descending",
                dataKey: "lastUpdatedTime",
                cell: (document: KhanmigoDocument) => {
                    const {lastUpdatedTime} = document;
                    return (
                        <LabelMedium style={styles.labelMedium}>
                            {formatDate(lastUpdatedTime)}
                        </LabelMedium>
                    );
                },
            },
            {
                label: "",
                cell: (document: KhanmigoDocument) => {
                    const {configName} = document;
                    const tool = findTool(configName);

                    return (
                        <DocumentActions
                            document={document}
                            onArchive={(threadId: string) => {
                                setArchivedThreadIDs(
                                    (s) => new Set([...s, threadId]),
                                );
                            }}
                            tool={tool}
                        />
                    );
                },
            },
        ];
    }, [tools, platform]);

    switch (status) {
        case "no-data":
            return (
                <StateComponent
                    state="empty"
                    setTab={setTab}
                    platform={platform}
                />
            );
        case "loading":
            return (
                <View style={styles.loadingContainer}>
                    <CircularSpinner size="medium" testId="spinner" />
                </View>
            );
        case "success":
            return (
                <>
                    {!filteredThreads || filteredThreads.length === 0 ? (
                        <StateComponent
                            state="empty"
                            setTab={setTab}
                            platform={platform}
                        />
                    ) : (
                        <View
                            testId="my-documents-view"
                            style={styles.container}
                        >
                            <SimpleTable
                                columns={columns}
                                data={filteredThreads as KhanmigoDocument[]}
                                defaultSortKey="lastUpdatedTime"
                                pageSize={10}
                                paginationAfterTable={true}
                            />
                        </View>
                    )}
                </>
            );
        case "error":
            return (
                <StateComponent
                    state="error"
                    setTab={setTab}
                    platform={platform}
                />
            );
        case "aborted":
            return (
                <StateComponent
                    state="error"
                    setTab={setTab}
                    platform={platform}
                />
            );
        default:
            return null;
    }
};

const styles = StyleSheet.create({
    links: {
        color: color.offBlack,
        padding: 0,
        ":hover": {
            color: color.blue,
        },
    },
    labelContainer: {
        display: "flex",
        alignItems: "center",
    },
    loadingContainer: {
        flex: 1,
        minHeight: 300,
        alignItems: "center",
        margin: spacing.large_24,
    },
    emptyContainer: {
        flex: 1,
        minHeight: 300,
        alignItems: "center",
    },
    container: {
        borderTop: `1px solid ${color.offBlack8}`,
        marginBottom: spacing.xxLarge_48,
    },
    iconContainer: {
        width: 24,
        height: 24,
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 4,
    },
    labelMedium: {
        color: color.offBlack64,
    },
    kebabMenu: {
        width: 160,
    },
});

export default compose(
    withConversionContext({
        activity: enums.Activity.NAVIGATING,
    }),
)(MyDocuments) as React.ComponentType<Props>;
